import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getAgentV2ConversationDetail } from '@/agent-v2/conversations/fetch/get-agent-v2-conversation-detail'
import { reportDroppedAgentV2References, sanitizeAgentV2References } from '@/agent-v2/references/agent-v2-reference-sanitizer'
import { AgentV2ReferencesActions } from '@/agent-v2/references/store/slice'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { useTitle } from '@/hooks/use-title'
import ReadOnlyMatterArtifact from '@/matter-artifacts/components/ReadOnlyMatterArtifact'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import { useAppDispatch } from '@/store/store-hooks'

/** View a workspace artifact without exposing its conversation or web matter navigation. */
export default function StandaloneMatterArtifactPage() {
  const { matterId = '', conversationId = '', versionId = '' } = useParams()
  const dispatch = useAppDispatch()
  const key = `${matterId}:${conversationId}:${versionId}`
  const [result, setResult] = useState<{ key: string; title: string | null; failed: boolean } | null>(null)
  const [attempt, setAttempt] = useState(0)
  const currentResult = result?.key === key ? result : null
  useTitle(currentResult?.title ?? 'Outlook draft')

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    void getAgentV2ConversationDetail(conversationId, controller.signal)
      .then((detail) => {
        if (cancelled) return
        const version = [...detail.artifact_versions, ...detail.turns.flatMap((turn) => turn.artifact_versions)].find((entry) => entry.id === versionId)
        if (detail.conversation.id !== conversationId || detail.conversation.matter_id !== matterId || !version) {
          setResult({ key, title: null, failed: true })
          return
        }
        const { accepted, dropped } = sanitizeAgentV2References(conversationId, detail.references)
        reportDroppedAgentV2References(conversationId, dropped)
        dispatch(AgentV2ReferencesActions.upsertReferences({ conversationId, references: accepted }))
        dispatch(MatterArtifactsActions.upsertMatterArtifactVersion(version))
        setResult({ key, title: version.name ?? 'Outlook draft', failed: false })
      })
      .catch(() => {
        if (!cancelled) setResult({ key, title: null, failed: true })
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [attempt, conversationId, dispatch, key, matterId, versionId])

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {!currentResult ? (
        <ContentViewerLoadingSpinner size="large" />
      ) : currentResult.failed ? (
        <ContentViewerErrorState
          message="Could not load this draft."
          onRetry={() => {
            setResult(null)
            setAttempt((value) => value + 1)
          }}
        />
      ) : (
        <>
          <header className="border-b border-brand-neutral-200 p-3">
            <h1 className="text-xl font-bold leading-7">{currentResult.title}</h1>
            <p className="text-sm text-brand-neutral-600">Read-only preview. Open the conversation in Paxton or Outlook to revise this reply.</p>
          </header>
          <ReadOnlyMatterArtifact conversationId={conversationId} matterArtifactVersionId={versionId} openSourcesInChatPanel={false} showOwnerBanner={false} />
        </>
      )}
    </div>
  )
}
